import { useQuery } from '@tanstack/react-query';
import { deviceApi, alarmApi, siteApi, zoneApi } from '../../services/api';

export const profileKeys = {
  stats: (orgId?: string) => ['profile', 'stats', orgId] as const,
};

export interface UserStats {
  devicesCount: number;
  activeDevicesCount: number;
  activeAlarmsCount: number;
  sitesCount: number;
  zonesCount: number;
}

export function useProfileStats(orgId?: string, includeStructure = true) {
  return useQuery<UserStats>({
    queryKey: profileKeys.stats(orgId),
    queryFn: async () => {
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);

      const [devices, alarms, sites, zones] = await Promise.all([
        deviceApi.getDevices(orgId || '').catch(() => []),
        alarmApi.getActiveAlarms(orgId!).catch(() => []),
        includeStructure ? siteApi.getSites(orgId).catch(() => []) : Promise.resolve([]),
        includeStructure ? zoneApi.getZones(orgId!).catch(() => []) : Promise.resolve([]),
      ]);

      const deviceList = Array.isArray(devices) ? devices : [];
      const activeDevices = deviceList.filter((d: any) => {
        if (d.lastSeenAt) {
          const lastSeen = new Date(d.lastSeenAt.endsWith('Z') ? d.lastSeenAt : d.lastSeenAt + 'Z');
          return lastSeen > fifteenMinutesAgo;
        }
        return d.status === 'online';
      });

      return {
        devicesCount: deviceList.length,
        activeDevicesCount: activeDevices.length,
        activeAlarmsCount: Array.isArray(alarms) ? alarms.length : 0,
        sitesCount: Array.isArray(sites) ? sites.length : 0,
        zonesCount: Array.isArray(zones) ? zones.length : 0,
      };
    },
    enabled: !!orgId,
    staleTime: 30_000,
  });
}
